import { Service, inject } from '@angular/core';

import { Observable, switchMap, map, catchError, throwError, tap, take, shareReplay, of } from 'rxjs';

import { ApolloClientService } from 'src/app/services/apollo-client.service';
import {
	RESULTS_TEST_RUNS_BY_TEST_SUITE,
	RESULTS_TEST_RUNS_BY_TEST_SPECIFICATION,
	RESULTS_TEST_RUNS_BY_TEST_CASE,
	RESULTS_TEST_RUN_MODELS_BY_DATES,
	RESULTS_COMPLETE_TEST_RUN,
	RESULTS_TEST_SUITES_FOR_TEST_SUITE,
	RESULTS_TEST_CASES_FOR_TEST_SUITE,
	RESULTS_OBJECTS_UNDER_TEST_FOR_TEST_RESULT,
	RESULTS_TEST_CASE_STEPS,
	RESULTS_PROPERTIES_FOR_TEST_RUN,
	RESULTS_PROPERTIES_FOR_TEST_RESULT,
	RESULTS_TEST_CASE_ALTERNATIVES,
	RESULTS_TEST_CASE_DETAILS,
	RESULTS_REQUIREMENTS_WITH_TEST_ANALYSIS,
	RESULTS_REQUIREMENTS_BY_TEST_CASE,
	RESULTS_SOPS_FOR_TEST_RUN,
	RESULTS_ECU_PARAMETERS_FOR_TEST_RUN,
	RESULTS_ECU_READOUTS_FOR_TEST_RUN,
	RESULTS_TEST_SUITE_ALTERNATIVES,
	RESULTS_TEST_SUITE,
	RESULTS_TEST_CASE,
	RESULTS_TEST_SPECIFICATION,
	RESULTS_ALL_ECUS,
} from 'src/app/graphql/queries/results.queries';
import { ResultsTestRunModel, ResultsCompleteTestRun, ResultsProperty } from 'src/app/models/results';
import { GraphQLResultsCompleteTestRunOutput, GraphQLResultsTestRunModelOutput } from 'src/app/graphql/models/output/results-complete-test-run.output.model';
import {
	GraphQLResultsTestRunHistoryBySuiteOutput,
	GraphQLResultsTestRunHistoryBySpecificationOutput,
	GraphQLResultsTestRunHistoryByTestCaseOutput,
} from 'src/app/graphql/models/output/results-test-run-history.output.model';
import { mapGraphQLResultsCompleteTestRun, mapGraphQLResultsTestRunModels } from 'src/app/graphql/mappers/results-complete-test-run.mapper';
import {
	mapGraphQLResultsTestRunsToHistoryEntries,
	mapGraphQLResultsTestSpecRunsToHistoryEntries,
	mapGraphQLResultsTestRunsForTestCaseToHistoryEntries,
} from 'src/app/graphql/mappers/results-test-run-to-history-entry.mapper';
import {
	mapGraphQLResultsProperties,
	mapGraphQLResultsTestSuites,
	mapGraphQLResultsTestCaseSummaries,
	mapGraphQLResultsTestCaseSteps,
	mapGraphQLResultsObjectsUnderTest,
	mapGraphQLResultsRequirementRows,
	mapGraphQLResultsSops,
	mapGraphQLResultsEcuParameters,
	mapGraphQLResultsTestSuiteAlternatives,
	mapGraphQLResultsTestCaseAlternatives,
	mapGraphQLResultsTestCaseDetails,
	mapGraphQLResultsTestSuiteFull,
	mapGraphQLResultsTestCaseFull,
	mapGraphQLResultsTestSpecification,
	mapGraphQLResultsAllEcus,
} from 'src/app/graphql/mappers/results.mappers';
import {
	GraphQLResultsPropertyOutput,
	GraphQLResultsTestSuiteOutput,
	GraphQLResultsTestCaseSummaryOutput,
	GraphQLResultsTestCaseStepOutput,
	GraphQLResultsObjectsUnderTestOutput,
	GraphQLResultsRequirementRowOutput,
	GraphQLResultsSopsOutput,
	GraphQLResultsEcuParameterOutput,
	GraphQLResultsEcuReadoutOutput,
	GraphQLResultsTestSuiteAlternativeOutput,
	GraphQLResultsTestCaseAlternativeOutput,
	GraphQLResultsTestCaseDetailsOutput,
	GraphQLResultsTestSuiteFullOutput,
	GraphQLResultsTestCaseFullOutput,
	GraphQLResultsTestSpecificationOutput,
	GraphQLResultsTestResultEcuOutput,
} from 'src/app/graphql/models/output/results.output.model';
import { ResultsTestSuite } from 'src/app/models/results/results-test-suite.model';
import { ResultsTestCase, ResultsTestCaseStep, ResultsTestCaseAlternative, ResultsEcu } from 'src/app/models/results/results-test-case.model';
import { ResultsTestSpecification } from 'src/app/models/results/results-test-specification.model';
import { ResultsTestResultEcu, ResultsTestResultOffboardComponent } from 'src/app/models/results/results-test-result.model';
import { ResultsSopsResponse } from 'src/app/models/results/results-sops.model';
import { ResultsRequirementRow } from 'src/app/models/results/results-requirement.model';
import { EcuParamItem } from 'src/app/models/results/ecu-param-item.model';
import { TestHistoryEntry } from 'src/app/models/test-history.model';
import { environment } from 'src/environments/environment';

/**
 * Low-level service that wraps all Results abstraction layer GraphQL queries.
 * Used by higher-level services (ResultsDataAdapterService, ResultsSuiteDetailsService)
 * to fetch test runs, test results, test suites, and trouble reports directly
 * from the Results database via AppSync.
 */
@Service()
export class ResultsService {
	private apolloClientService = inject(ApolloClientService);

	private isDev = !environment.IsProduction;

	/**
	 * Full ECU catalogue, cached for the app's lifetime (mirrors the Results client's EcuService.allEcus$).
	 * Fetched lazily and shared, so the test-run properties panel only pays for it once per session and
	 * only when a run actually contains an EcuForSoftwareVersion property. Errors resolve to an empty
	 * list so property display degrades to the raw id rather than failing.
	 */
	private allEcus$?: Observable<ResultsEcu[]>;

	/** Returns the shared ECU catalogue, initialising the cached stream on first use. */
	public getAllEcus$(): Observable<ResultsEcu[]> {
		if (!this.allEcus$) {
			this.allEcus$ = this.apolloClientService.apolloClient$.pipe(
				take(1),
				switchMap(client =>
					client.query<{ ecus: GraphQLResultsTestResultEcuOutput[] }>({
						query: RESULTS_ALL_ECUS,
						fetchPolicy: 'network-only',
					}),
				),
				map(result => mapGraphQLResultsAllEcus(result.data?.ecus)),
				shareReplay({ bufferSize: 1, refCount: false }),
				catchError((error: any) => {
					console.error('[ResultsService] Error fetching ECUs:', error);
					return of([] as ResultsEcu[]);
				}),
			);
		}
		return this.allEcus$;
	}

	public getTestRunModelsByDates$(startDate: string, endDate: string): Observable<ResultsTestRunModel[]> {
		if (this.isDev) {
			console.log('[ResultsService] Fetching test runs by dates:', { startDate, endDate });
		}

		return this.apolloClientService.apolloClient$.pipe(
			take(1),
			switchMap(client =>
				client.query<{ resultsTestRunModelByDates: GraphQLResultsTestRunModelOutput[] }>({
					query: RESULTS_TEST_RUN_MODELS_BY_DATES,
					variables: { startDate, endDate },
					fetchPolicy: 'network-only',
				}),
			),
			tap(result => this.isDev && console.log('[ResultsService] Raw test runs result:', result)),
			map(result => {
				if (!result.data?.resultsTestRunModelByDates) {
					throw new Error('No test run data returned from Results API');
				}
				return mapGraphQLResultsTestRunModels(result.data.resultsTestRunModelByDates);
			}),
			catchError((error: any) => {
				console.error('[ResultsService] Error fetching test runs by dates:', error);
				return throwError(() => new Error(`Failed to fetch test runs: ${error.message || 'Unknown error'}`));
			}),
		);
	}

	/**
	 * Fetches all test runs for a test suite (the Results client's "Test runs for test suite" list)
	 * and maps them into TestHistoryEntry[] for the shared test-history card. The Results list has no
	 * per-run status or sub-suite counts, so subSuites is empty and the card is fed with showStatus=false.
	 * Returned newest-execution-first to match how the card expects entries (it reverses for display).
	 *
	 * @param testSuiteUid The test suite UID (not the test run UID).
	 */
	public getTestRunsByTestSuite$(testSuiteUid: string): Observable<TestHistoryEntry[]> {
		return this.apolloClientService.apolloClient$.pipe(
			take(1),
			switchMap(client =>
				client.query<{ resultsTestRunModelsByTestSuite: GraphQLResultsTestRunHistoryBySuiteOutput[] }>({
					query: RESULTS_TEST_RUNS_BY_TEST_SUITE,
					variables: { uid: testSuiteUid },
					fetchPolicy: 'network-only',
				}),
			),
			map(result => mapGraphQLResultsTestRunsToHistoryEntries(result.data?.resultsTestRunModelsByTestSuite ?? [])),
			catchError((error: any) => {
				console.error('[ResultsService] Error fetching test runs by test suite:', error);
				return throwError(() => new Error(`Failed to fetch test runs for suite: ${error.message || 'Unknown error'}`));
			}),
		);
	}

	/**
	 * Specification counterpart of getTestRunsByTestSuite: fetches all test runs for a test
	 * specification (the Results client's "test specifications" page) and maps them into
	 * TestHistoryEntry[] for the shared test-history card. Used when the viewed run is a
	 * specification run (testRunType 410) rather than a test suite.
	 *
	 * @param testSpecificationUid The test specification UID (not the test run UID).
	 */
	public getTestRunsByTestSpecification$(testSpecificationUid: string): Observable<TestHistoryEntry[]> {
		return this.apolloClientService.apolloClient$.pipe(
			take(1),
			switchMap(client =>
				client.query<{ resultsTestRunModelsByTestSpecification: GraphQLResultsTestRunHistoryBySpecificationOutput[] }>({
					query: RESULTS_TEST_RUNS_BY_TEST_SPECIFICATION,
					variables: { uid: testSpecificationUid },
					fetchPolicy: 'network-only',
				}),
			),
			map(result => mapGraphQLResultsTestSpecRunsToHistoryEntries(result.data?.resultsTestRunModelsByTestSpecification ?? [])),
			catchError((error: any) => {
				console.error('[ResultsService] Error fetching test runs by test specification:', error);
				return throwError(() => new Error(`Failed to fetch test runs for specification: ${error.message || 'Unknown error'}`));
			}),
		);
	}

	/**
	 * Fetches all test runs that ran a given TEST CASE (the Results client's "results for test case"
	 * page) and maps them into TestHistoryEntry[] for the shared card's test-case column set.
	 *
	 * @param testCaseUid The test case UID.
	 */
	public getTestRunsByTestCase$(testCaseUid: string): Observable<TestHistoryEntry[]> {
		return this.apolloClientService.apolloClient$.pipe(
			take(1),
			switchMap(client =>
				client.query<{ resultsTestRunModelsByTestCase: GraphQLResultsTestRunHistoryByTestCaseOutput[] }>({
					query: RESULTS_TEST_RUNS_BY_TEST_CASE,
					variables: { uid: testCaseUid },
					fetchPolicy: 'network-only',
				}),
			),
			map(result => mapGraphQLResultsTestRunsForTestCaseToHistoryEntries(result.data?.resultsTestRunModelsByTestCase ?? [])),
			catchError((error: any) => {
				console.error('[ResultsService] Error fetching test runs by test case:', error);
				return throwError(() => new Error(`Failed to fetch test runs for test case: ${error.message || 'Unknown error'}`));
			}),
		);
	}

	public getCompleteTestRun$(uid: string): Observable<ResultsCompleteTestRun> {
		if (this.isDev) {
			console.log('[ResultsService] Fetching complete test run:', uid);
		}

		return this.apolloClientService.apolloClient$.pipe(
			take(1),
			switchMap(client =>
				client.query<{ resultsCompleteTestRun: GraphQLResultsCompleteTestRunOutput }>({
					query: RESULTS_COMPLETE_TEST_RUN,
					variables: { uid },
					fetchPolicy: 'network-only',
				}),
			),
			map(result => {
				if (!result.data?.resultsCompleteTestRun) {
					throw new Error('No complete test run data returned');
				}
				return mapGraphQLResultsCompleteTestRun(result.data.resultsCompleteTestRun);
			}),
			catchError((error: any) => {
				console.error('[ResultsService] Error fetching complete test run:', error);
				return throwError(() => new Error(`Failed to fetch complete test run: ${error.message || 'Unknown error'}`));
			}),
		);
	}

	public getTestSuitesForTestSuite$(uid: string, version?: number): Observable<ResultsTestSuite[]> {
		return this.apolloClientService.apolloClient$.pipe(
			take(1),
			switchMap(client =>
				client.query<{ resultsTestSuitesForTestSuite: GraphQLResultsTestSuiteOutput[] }>({
					query: RESULTS_TEST_SUITES_FOR_TEST_SUITE,
					variables: { uid, version: version ?? null },
					fetchPolicy: 'network-only',
				}),
			),
			map(result => mapGraphQLResultsTestSuites(result.data?.resultsTestSuitesForTestSuite)),
			catchError((error: any) => {
				console.error('[ResultsService] Error fetching child test suites:', error);
				return throwError(() => new Error(`Failed to fetch child test suites: ${error.message || 'Unknown error'}`));
			}),
		);
	}

	public getTestCasesForTestSuite$(testSuiteUid: string, testSuiteVersion?: number): Observable<ResultsTestCase[]> {
		return this.apolloClientService.apolloClient$.pipe(
			take(1),
			switchMap(client =>
				client.query<{ resultsTestCasesForTestSuite: GraphQLResultsTestCaseSummaryOutput[] }>({
					query: RESULTS_TEST_CASES_FOR_TEST_SUITE,
					variables: { testSuiteUid, testSuiteVersion: testSuiteVersion ?? null },
					fetchPolicy: 'network-only',
				}),
			),
			map(result => mapGraphQLResultsTestCaseSummaries(result.data?.resultsTestCasesForTestSuite)),
			catchError((error: any) => {
				console.error('[ResultsService] Error fetching test cases for suite:', error);
				return throwError(() => new Error(`Failed to fetch test cases: ${error.message || 'Unknown error'}`));
			}),
		);
	}

	public getTestCaseSteps$(testCaseUid: string, testCaseVersion?: number): Observable<ResultsTestCaseStep[]> {
		return this.apolloClientService.apolloClient$.pipe(
			take(1),
			switchMap(client =>
				client.query<{ resultsTestCaseSteps: GraphQLResultsTestCaseStepOutput[] }>({
					query: RESULTS_TEST_CASE_STEPS,
					variables: { testCaseUid, testCaseVersion: testCaseVersion ?? null },
					fetchPolicy: 'network-only',
				}),
			),
			map(result => mapGraphQLResultsTestCaseSteps(result.data?.resultsTestCaseSteps)),
			catchError((error: any) => {
				console.error('[ResultsService] Error fetching test case steps:', error);
				return throwError(() => new Error(`Failed to fetch test case steps: ${error.message || 'Unknown error'}`));
			}),
		);
	}

	/**
	 * Fetches the objects under test (ECUs and offboard components) linked to a
	 * single test result. These are result-level links recorded at execution time,
	 * not the test case definition's ECUs, and are only available via the
	 * single-result resultsTestResult query.
	 * @param testResultUid
	 */
	public getObjectsUnderTestForTestResult$(testResultUid: string): Observable<{ ecus: ResultsTestResultEcu[]; offboardComponents: ResultsTestResultOffboardComponent[] }> {
		return this.apolloClientService.apolloClient$.pipe(
			take(1),
			switchMap(client =>
				client.query<{ resultsTestResult: GraphQLResultsObjectsUnderTestOutput | null }>({
					query: RESULTS_OBJECTS_UNDER_TEST_FOR_TEST_RESULT,
					variables: { uid: testResultUid },
					fetchPolicy: 'network-only',
				}),
			),
			map(result => mapGraphQLResultsObjectsUnderTest(result.data?.resultsTestResult)),
			catchError((error: any) => {
				console.error('[ResultsService] Error fetching objects under test:', error);
				return throwError(() => new Error(`Failed to fetch objects under test: ${error.message || 'Unknown error'}`));
			}),
		);
	}

	public getPropertiesForTestRun$(testRunUid: string): Observable<ResultsProperty[]> {
		return this.apolloClientService.apolloClient$.pipe(
			take(1),
			switchMap(client =>
				client.query<{ resultsPropertiesForTestRun: GraphQLResultsPropertyOutput[] }>({
					query: RESULTS_PROPERTIES_FOR_TEST_RUN,
					variables: { testRunUid },
					fetchPolicy: 'network-only',
				}),
			),
			map(result => mapGraphQLResultsProperties(result.data?.resultsPropertiesForTestRun)),
			catchError((error: any) => {
				console.error('[ResultsService] Error fetching properties for test run:', error);
				return throwError(() => new Error(`Failed to fetch properties: ${error.message || 'Unknown error'}`));
			}),
		);
	}

	public getPropertiesForTestResult$(testResultUid: string): Observable<ResultsProperty[]> {
		return this.apolloClientService.apolloClient$.pipe(
			take(1),
			switchMap(client =>
				client.query<{ resultsPropertiesForTestResult: GraphQLResultsPropertyOutput[] }>({
					query: RESULTS_PROPERTIES_FOR_TEST_RESULT,
					variables: { testResultUid },
					fetchPolicy: 'network-only',
				}),
			),
			map(result => mapGraphQLResultsProperties(result.data?.resultsPropertiesForTestResult)),
			catchError((error: any) => {
				console.error('[ResultsService] Error fetching properties for test result:', error);
				return throwError(() => new Error(`Failed to fetch properties: ${error.message || 'Unknown error'}`));
			}),
		);
	}

	public getTestCaseAlternatives$(testCaseUid: string, testCaseVersion?: number): Observable<ResultsTestCaseAlternative[]> {
		return this.apolloClientService.apolloClient$.pipe(
			take(1),
			switchMap(client =>
				client.query<{ resultsTestCaseAlternatives: GraphQLResultsTestCaseAlternativeOutput[] }>({
					query: RESULTS_TEST_CASE_ALTERNATIVES,
					variables: { testCaseUid, testCaseVersion: testCaseVersion ?? null },
					fetchPolicy: 'network-only',
				}),
			),
			map(result => mapGraphQLResultsTestCaseAlternatives(result.data?.resultsTestCaseAlternatives)),
			catchError((error: any) => {
				console.error('[ResultsService] Error fetching test case alternatives:', error);
				return throwError(() => new Error(`Failed to fetch test case alternatives: ${error.message || 'Unknown error'}`));
			}),
		);
	}

	public getTestCaseDetails$(testCaseUid: string, testCaseVersion?: number): Observable<ResultsTestCase | null> {
		return this.apolloClientService.apolloClient$.pipe(
			take(1),
			switchMap(client =>
				client.query<{ resultsTestCaseDetails: GraphQLResultsTestCaseDetailsOutput | null }>({
					query: RESULTS_TEST_CASE_DETAILS,
					variables: { testCaseUid, testCaseVersion: testCaseVersion ?? null },
					fetchPolicy: 'network-only',
				}),
			),
			map(result => (result.data?.resultsTestCaseDetails ? mapGraphQLResultsTestCaseDetails(result.data.resultsTestCaseDetails) : null)),
			catchError((error: any) => {
				console.error('[ResultsService] Error fetching test case details:', error);
				return throwError(() => new Error(`Failed to fetch test case details: ${error.message || 'Unknown error'}`));
			}),
		);
	}

	public getRequirementsWithTestAnalysis$(testResultUid: string): Observable<ResultsRequirementRow[]> {
		return this.apolloClientService.apolloClient$.pipe(
			take(1),
			switchMap(client =>
				client.query<{ resultsRequirementsWithTestAnalysis: GraphQLResultsRequirementRowOutput[] }>({
					query: RESULTS_REQUIREMENTS_WITH_TEST_ANALYSIS,
					variables: { testResultUid },
					fetchPolicy: 'network-only',
				}),
			),
			map(result => mapGraphQLResultsRequirementRows(result.data?.resultsRequirementsWithTestAnalysis)),
			catchError((error: any) => {
				console.error('[ResultsService] Error fetching requirements:', error);
				return throwError(() => new Error(`Failed to fetch requirements: ${error.message || 'Unknown error'}`));
			}),
		);
	}

	public getRequirementsByTestCase$(testCaseUid: string, testCaseVersion?: number): Observable<ResultsRequirementRow[]> {
		return this.apolloClientService.apolloClient$.pipe(
			take(1),
			switchMap(client =>
				client.query<{ resultsRequirementsWithTestAnalysisByTestCase: GraphQLResultsRequirementRowOutput[] }>({
					query: RESULTS_REQUIREMENTS_BY_TEST_CASE,
					variables: { testCaseUid, testCaseVersion: testCaseVersion ?? null },
					fetchPolicy: 'network-only',
				}),
			),
			map(result => mapGraphQLResultsRequirementRows(result.data?.resultsRequirementsWithTestAnalysisByTestCase)),
			catchError((error: any) => {
				console.error('[ResultsService] Error fetching requirements by test case:', error);
				return throwError(() => new Error(`Failed to fetch requirements: ${error.message || 'Unknown error'}`));
			}),
		);
	}

	public getSopsForTestRun$(testRunUid: string): Observable<ResultsSopsResponse | null> {
		return this.apolloClientService.apolloClient$.pipe(
			take(1),
			switchMap(client =>
				client.query<{ resultsSopsForTestRun: GraphQLResultsSopsOutput | null }>({
					query: RESULTS_SOPS_FOR_TEST_RUN,
					variables: { testRunUid },
					fetchPolicy: 'network-only',
				}),
			),
			map(result => mapGraphQLResultsSops(result.data?.resultsSopsForTestRun)),
			catchError((error: any) => {
				console.error('[ResultsService] Error fetching SOPs:', error);
				return throwError(() => new Error(`Failed to fetch SOPs: ${error.message || 'Unknown error'}`));
			}),
		);
	}

	public getEcuParametersForTestRun$(testRunUid: string): Observable<EcuParamItem[]> {
		return this.apolloClientService.apolloClient$.pipe(
			take(1),
			switchMap(client =>
				client.query<{ resultsEcuParametersForTestRun: GraphQLResultsEcuParameterOutput[] }>({
					query: RESULTS_ECU_PARAMETERS_FOR_TEST_RUN,
					variables: { testRunUid },
					fetchPolicy: 'network-only',
				}),
			),
			map(result => mapGraphQLResultsEcuParameters(result.data?.resultsEcuParametersForTestRun)),
			catchError((error: any) => {
				console.error('[ResultsService] Error fetching ECU parameters:', error);
				return throwError(() => new Error(`Failed to fetch ECU parameters: ${error.message || 'Unknown error'}`));
			}),
		);
	}

	public getEcuReadoutsForTestRun$(testRunUid: string): Observable<GraphQLResultsEcuReadoutOutput[]> {
		return this.apolloClientService.apolloClient$.pipe(
			take(1),
			switchMap(client =>
				client.query<{ resultsEcuReadoutsForTestRun: GraphQLResultsEcuReadoutOutput[] }>({
					query: RESULTS_ECU_READOUTS_FOR_TEST_RUN,
					variables: { testRunUid },
					fetchPolicy: 'network-only',
				}),
			),
			map(result => result.data?.resultsEcuReadoutsForTestRun ?? []),
			catchError((error: any) => {
				console.error('[ResultsService] Error fetching ECU readouts:', error);
				return throwError(() => new Error(`Failed to fetch ECU readouts: ${error.message || 'Unknown error'}`));
			}),
		);
	}

	public getTestSuiteAlternatives$(testSuiteUid: string, testSuiteVersion?: number): Observable<ResultsTestCaseAlternative[]> {
		return this.apolloClientService.apolloClient$.pipe(
			take(1),
			switchMap(client =>
				client.query<{ resultsTestSuiteAlternativesForTestSuite: GraphQLResultsTestSuiteAlternativeOutput[] }>({
					query: RESULTS_TEST_SUITE_ALTERNATIVES,
					variables: { testSuiteUid, testSuiteVersion: testSuiteVersion ?? null },
					fetchPolicy: 'network-only',
				}),
			),
			map(result => mapGraphQLResultsTestSuiteAlternatives(result.data?.resultsTestSuiteAlternativesForTestSuite)),
			catchError((error: any) => {
				console.error('[ResultsService] Error fetching test suite alternatives:', error);
				return throwError(() => new Error(`Failed to fetch test suite alternatives: ${error.message || 'Unknown error'}`));
			}),
		);
	}

	/**
	 * Full test suite record for the details page; omit version to get the latest.
	 * @param uid
	 * @param version
	 */
	public getTestSuite$(uid: string, version?: number): Observable<ResultsTestSuite | null> {
		return this.apolloClientService.apolloClient$.pipe(
			take(1),
			switchMap(client =>
				client.query<{ resultsTestSuite: GraphQLResultsTestSuiteFullOutput | null }>({
					query: RESULTS_TEST_SUITE,
					variables: { uid, version: version ?? null },
					fetchPolicy: 'network-only',
				}),
			),
			map(result => (result.data?.resultsTestSuite ? mapGraphQLResultsTestSuiteFull(result.data.resultsTestSuite) : null)),
			catchError((error: any) => {
				console.error('[ResultsService] Error fetching test suite:', error);
				return throwError(() => new Error(`Failed to fetch test suite: ${error.message || 'Unknown error'}`));
			}),
		);
	}

	/**
	 * Full test case record for the details page; omit version to get the latest.
	 * @param uid
	 * @param version
	 */
	public getTestCase$(uid: string, version?: number): Observable<ResultsTestCase | null> {
		return this.apolloClientService.apolloClient$.pipe(
			take(1),
			switchMap(client =>
				client.query<{ resultsTestCase: GraphQLResultsTestCaseFullOutput | null }>({
					query: RESULTS_TEST_CASE,
					variables: { uid, version: version ?? null },
					fetchPolicy: 'network-only',
				}),
			),
			map(result => (result.data?.resultsTestCase ? mapGraphQLResultsTestCaseFull(result.data.resultsTestCase) : null)),
			catchError((error: any) => {
				console.error('[ResultsService] Error fetching test case:', error);
				return throwError(() => new Error(`Failed to fetch test case: ${error.message || 'Unknown error'}`));
			}),
		);
	}

	/**
	 * Full test specification record for the details page; omit version to get the latest.
	 * @param uid
	 * @param version
	 */
	public getTestSpecification$(uid: string, version?: number): Observable<ResultsTestSpecification | null> {
		return this.apolloClientService.apolloClient$.pipe(
			take(1),
			switchMap(client =>
				client.query<{ resultsTestSpecification: GraphQLResultsTestSpecificationOutput | null }>({
					query: RESULTS_TEST_SPECIFICATION,
					variables: { uid, version: version ?? null },
					fetchPolicy: 'network-only',
				}),
			),
			map(result => (result.data?.resultsTestSpecification ? mapGraphQLResultsTestSpecification(result.data.resultsTestSpecification) : null)),
			catchError((error: any) => {
				console.error('[ResultsService] Error fetching test specification:', error);
				return throwError(() => new Error(`Failed to fetch test specification: ${error.message || 'Unknown error'}`));
			}),
		);
	}
}
